import { Badge, Button, SkeletonRows, SlideOver, cn, useToast } from '@ambernord/ui';
import { FileQuestion, Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';
import { formatNumber, productName } from '../../lib/format.js';
import { BreakdownTable } from './BreakdownTable.js';
import {
  type AdminOfferDetail,
  type Combination,
  type CostBreakdown,
  type OfferRow,
  type ProductItem,
  errorKey,
  shortId,
  supplierLabel,
} from './types.js';

/**
 * Admina komponēšanas panelis: GET /:id/combinations (supplier×carrier, cheapest-first,
 * ar maržu — admin projekcija §4.9) → izvēle → POST /:id/draft → sadalījums + Send.
 */
export function ComposePanel({
  offer,
  productMap,
  carrierNames,
  onClose,
  onChanged,
}: {
  offer: OfferRow;
  productMap: Map<string, ProductItem>;
  carrierNames: Map<string, string>;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation('offers');
  const { toast } = useToast();
  const locale = i18n.language;

  const [detail, setDetail] = useState<AdminOfferDetail | null>(null);
  const [combos, setCombos] = useState<Combination[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [picking, setPicking] = useState<number | null>(null);
  const [drafted, setDrafted] = useState<CostBreakdown | null>(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let active = true;
    void (async () => {
      const [detailRes, combosRes] = await Promise.all([
        api.get<AdminOfferDetail>(`/api/offers/${offer.id}`),
        api.get<{ combinations?: Combination[]; error?: string }>(
          `/api/offers/${offer.id}/combinations`,
        ),
      ]);
      if (!active) return;
      if (detailRes.ok) setDetail(detailRes.data);
      if (combosRes.ok) setCombos(combosRes.data.combinations ?? []);
      else setLoadError(t(errorKey(combosRes.data.error)));
    })();
    return () => {
      active = false;
    };
  }, [offer.id, t]);

  async function pick(combo: Combination, index: number) {
    setPicking(index);
    try {
      const res = await api.post<{ breakdown?: CostBreakdown; error?: string }>(
        `/api/offers/${offer.id}/draft`,
        {
          supplierTenantId: combo.supplierTenantId,
          ...(combo.carrierTenantId ? { carrierTenantId: combo.carrierTenantId } : {}),
          shippingMode: combo.shippingMode,
          incoterm: combo.incoterm,
        },
      );
      if (!res.ok) {
        toast(t(errorKey(res.data.error)), 'error');
        return;
      }
      toast(t('toasts.drafted'), 'success');
      setDrafted(res.data.breakdown ?? null);
      onChanged();
    } finally {
      setPicking(null);
    }
  }

  async function send() {
    setSending(true);
    try {
      const res = await api.post<{ error?: string }>(`/api/offers/${offer.id}/send`);
      if (!res.ok) {
        toast(t(errorKey(res.data.error)), 'error');
        return;
      }
      toast(t('toasts.sent'), 'success');
      onChanged();
      onClose();
    } finally {
      setSending(false);
    }
  }

  function carrierLabel(combo: Combination): string {
    if (!combo.carrierTenantId) return t('labels.supplierArranged');
    return carrierNames.get(combo.carrierTenantId) ?? shortId(combo.carrierTenantId);
  }

  return (
    <SlideOver
      open
      onClose={onClose}
      title={`${t('actions.compose')} — ${offer.offerNo}`}
      className="max-w-3xl"
    >
      <div className="flex flex-1 flex-col gap-5">
        {detail && detail.lines.length > 0 ? (
          <section>
            <h3 className="mb-1 text-sm font-semibold text-slate-900 dark:text-white">
              {t('labels.lines')}
            </h3>
            <ul className="text-sm text-slate-600 dark:text-slate-300">
              {detail.lines.map((l) => (
                <li key={l.id}>
                  {productName(productMap.get(l.productId)?.namesI18n, locale, l.productId)} —{' '}
                  {formatNumber(l.qty, locale)} {l.unit}
                </li>
              ))}
            </ul>
            {detail.offer.notes ? (
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {t('labels.notes')}: {detail.offer.notes}
              </p>
            ) : null}
          </section>
        ) : null}

        {drafted ? (
          <section className="flex flex-col gap-4">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
              {t('labels.breakdown')}
            </h3>
            <BreakdownTable
              components={drafted.components}
              currency={drafted.currency}
              subtotal={drafted.subtotal}
              total={drafted.total}
            />
            <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-800">
              <Button variant="ghost" onClick={onClose}>
                {t('actions.close')}
              </Button>
              <Button disabled={sending} onClick={() => void send()}>
                <Send className="h-4 w-4" aria-hidden />
                {sending ? t('loading') : t('actions.send')}
              </Button>
            </div>
          </section>
        ) : (
          <section className="flex flex-col gap-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                {t('labels.combinations')}
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {t('labels.combinationsHint')}
              </p>
            </div>

            {loadError ? (
              <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                {loadError}
              </p>
            ) : combos === null ? (
              <SkeletonRows rows={4} />
            ) : combos.length === 0 ? (
              <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-slate-300 px-4 py-8 text-center dark:border-slate-700">
                <FileQuestion className="h-6 w-6 text-slate-400" aria-hidden />
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
                  {t('empty.combinationsTitle')}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t('empty.combinationsHint')}
                </p>
              </div>
            ) : (
              <ol className="flex flex-col gap-2">
                {combos.map((combo, i) => {
                  const cheapest = i === 0;
                  const key = `${combo.supplierTenantId}-${combo.carrierTenantId ?? 'none'}-${combo.incoterm}`;
                  return (
                    <li
                      key={key}
                      className={cn(
                        'rounded-lg border border-slate-200 p-3 dark:border-slate-800',
                        cheapest &&
                          'border-emerald-400 bg-emerald-50/50 ring-1 ring-emerald-400 dark:border-emerald-600 dark:bg-emerald-950/30 dark:ring-emerald-600',
                      )}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-900 dark:text-white">
                            <span className="text-xs font-semibold text-slate-400 dark:text-slate-500">
                              {i + 1}.
                            </span>
                            {t('labels.supplier')} {supplierLabel(combo)}
                            <span className="text-slate-400 dark:text-slate-500">×</span>
                            {carrierLabel(combo)}
                            {cheapest ? <Badge tone="green">{t('labels.cheapest')}</Badge> : null}
                          </p>
                          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                            {t(`modes.${combo.shippingMode}`, { defaultValue: combo.shippingMode })}{' '}
                            · {t(`incoterms.${combo.incoterm}`, { defaultValue: combo.incoterm })} ·{' '}
                            {t('labels.margin')}:{' '}
                            {combo.breakdown.marginPct != null
                              ? `${formatNumber(combo.breakdown.marginPct, locale)}%`
                              : '—'}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <p className="text-base font-bold tabular-nums text-slate-900 dark:text-white">
                            {formatNumber(combo.total, locale)} {combo.currency}
                          </p>
                          <Button
                            size="sm"
                            variant={cheapest ? 'primary' : 'secondary'}
                            disabled={picking !== null}
                            onClick={() => void pick(combo, i)}
                          >
                            {picking === i ? t('loading') : t('actions.pick')}
                          </Button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </section>
        )}
      </div>
    </SlideOver>
  );
}
